import React, { useMemo, useState } from "react";
import {
  Datagrid,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useListContext,
} from "react-admin";
import {
  Avatar,
  Box,
  Button,
  Chip,
  MenuItem,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import { formatDate } from "../../utils/common";
import { getBillStatusChoices, getBillStatusColor, getBillStatusTitle } from "../../constants/BillStatus";
import { getBillTypeChoices, getBillTypeColor, getBillTypeTitle } from "../../constants/BillType";
import OnlineBillDetailDialog from "./online_bills_detail";
import {
  BillingCargoCard,
  BillingTruckCard,
  getBillingCargoId,
  mergeBillingCargoSummary,
  normalizeBillingCargoSummary,
  parseBillingCargo,
  parseBillingTruck,
} from "./online_bills_shared";

type BillingRecord = {
  id: string | number;
  [key: string]: unknown;
};

const STATUS_ALL_SENTINEL = 999;

const billStatusChoices = [
  { value: STATUS_ALL_SENTINEL, label: "全部状态" },
  ...getBillStatusChoices()
    .filter((item) => item.id !== 100)
    .map((item) => ({
      value: item.id,
      label: item.name,
    })),
];

const billTypeChoices = [
  { value: -1, label: "全部类型" },
  ...getBillTypeChoices()
    .filter((item) => item.id >= 0)
    .map((item) => ({
      value: item.id,
      label: item.name,
    })),
];

const ListActions: React.FC = () => {
  const { filterValues, setFilters } = useListContext();
  const [draftType, setDraftType] = useState<number>(Number(filterValues.type));
  const [draftStatus, setDraftStatus] = useState<number>(filterValues.status != null ? Number(filterValues.status) : STATUS_ALL_SENTINEL);
  const [draftBillId, setDraftBillId] = useState<string>(parseStringValue(filterValues._id));

  React.useEffect(() => {
    setDraftType(Number(filterValues.type));
    setDraftStatus(filterValues.status != null ? Number(filterValues.status) : STATUS_ALL_SENTINEL);
    setDraftBillId(parseStringValue(filterValues._id));
  }, [filterValues._id, filterValues.status, filterValues.type]);

  const handleSearch = () => {
    const nextFilters = {
      ...filterValues,
      type: draftType >= 0 ? draftType : undefined,
      status: draftStatus !== STATUS_ALL_SENTINEL ? draftStatus : undefined,
      _id: draftBillId.trim() || undefined,
    };
    setFilters(nextFilters, undefined);
  };

  const handleReset = () => {
    setDraftType(-1);
    setDraftStatus(STATUS_ALL_SENTINEL);
    setDraftBillId("");
    const nextFilters = { ...filterValues } as Record<string, unknown>;
    delete nextFilters.type;
    delete nextFilters.status;
    delete nextFilters._id;
    setFilters(nextFilters, undefined);
  };

  const handleBillIdKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSearch();
    }
  };

  return (
    <TopToolbar sx={{ width: "100%", alignItems: "center", gap: 1, flexWrap: "wrap", px: 2, py: 1 }}>
      <MuiTextField
        select
        size="small"
        label="账单类型"
        value={draftType}
        onChange={(event) => setDraftType(Number(event.target.value))}
        sx={{ minWidth: 180 }}
      >
        {billTypeChoices.map((item) => (
          <MenuItem key={item.value || "all"} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </MuiTextField>
      <MuiTextField
        select
        size="small"
        label="状态"
        value={draftStatus}
        onChange={(event) => setDraftStatus(Number(event.target.value))}
        sx={{ minWidth: 180 }}
      >
        {billStatusChoices.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            {item.label}
          </MenuItem>
        ))}
      </MuiTextField>
      <MuiTextField
        size="small"
        label="账单ID"
        value={draftBillId}
        onChange={(event) => setDraftBillId(event.target.value)}
        onKeyDown={handleBillIdKeyDown}
        sx={{ minWidth: 240 }}
      />
      <Button variant="contained" onClick={handleSearch}>查询</Button>
      <Button onClick={handleReset}>重置</Button>
    </TopToolbar>
  );
};

const getFirstDefined = (values: Array<unknown>): unknown => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

const parseStringValue = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const parseNumberValue = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const formatFenToYuan = (value: unknown): string => {
  const fen = parseNumberValue(value);
  if (fen === null) return "-";
  const yuan = fen / 100;
  const normalized = Math.abs(yuan) < 1e-9 ? 0 : yuan;
  return `${normalized.toFixed(2)} 元`;
};

type UserRecord = Record<string, unknown> & { id?: string; _id?: string };

const parseUserRecord = (raw: unknown): UserRecord | null => {
  if (raw === null || raw === undefined) return null;
  if (Array.isArray(raw)) {
    const first = raw[0];
    return parseUserRecord(first);
  }
  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    if (record.data !== undefined) {
      return parseUserRecord(record.data);
    }
    return record as UserRecord;
  }
  return null;
};

const getUserDisplayName = (record: UserRecord | null): string => {
  if (!record) return "";
  const name = record.name ?? record.username ?? record.realname ?? record.nickname;
  return parseStringValue(name);
};

const getUserDisplayAvatar = (record: UserRecord | null): string => {
  if (!record) return "";
  const avatar = record.avatar ?? record.photo ?? record.headimgurl;
  return parseStringValue(avatar);
};

const UsersContext = React.createContext<Record<string, UserRecord> | null>(null);

const normalizeUserId = (raw: unknown): string => parseStringValue(raw);

const getUserIdFromRecord = (record: UserRecord): string => {
  const id = record.id ?? record._id;
  return parseStringValue(id);
};

const UserInlineCell: React.FC<{ userId: string }> = ({ userId }) => {
  const users = React.useContext(UsersContext);
  const normalizedId = useMemo(() => normalizeUserId(userId), [userId]);
  const user = users && normalizedId ? users[normalizedId] : null;
  const avatar = getUserDisplayAvatar(user);
  const name = getUserDisplayName(user);
  
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 260 }}>
      <Avatar src={avatar || undefined} alt={name || "用户"} sx={{ width: 28, height: 28 }} />
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Typography variant="body2" sx={{ lineHeight: 1.2, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {name || "-"}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {normalizedId || "-"}
        </Typography>
      </Box>
    </Box>
  );
};

const BillsDatagrid: React.FC = () => {
  const dataProvider = useDataProvider();
  const { data } = useListContext<BillingRecord>();
  const [usersById, setUsersById] = useState<Record<string, UserRecord>>({});
  const [cargosById, setCargosById] = useState<Record<string, NonNullable<ReturnType<typeof parseBillingCargo>>>>({});
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [cargosLoading, setCargosLoading] = useState<boolean>(false);
  const [selectedBillId, setSelectedBillId] = useState<string>("");
  const [detailOpen, setDetailOpen] = useState<boolean>(false);

  const userIds = useMemo(() => {
    const ids = new Set<string>();
    for (const record of Object.values(data ?? {})) {
      const payerId = normalizeUserId(getFirstDefined([(record as any).payer, (record as any).payer_id]));
      const payeeId = normalizeUserId(getFirstDefined([(record as any).payee, (record as any).payee_id]));
      if (payerId) ids.add(payerId);
      if (payeeId) ids.add(payeeId);
    }
    return Array.from(ids);
  }, [data]);

  const cargoIds = useMemo(() => {
    const ids = new Set<string>();
    for (const record of Object.values(data ?? {})) {
      const cargoId = getBillingCargoId(record as BillingRecord);
      if (cargoId) ids.add(cargoId);
    }
    return Array.from(ids);
  }, [data]);

  React.useEffect(() => {
    if (userIds.length === 0) return;
    const missingIds = userIds.filter((id) => !usersById[id]);
    if (missingIds.length === 0) return;
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const resp = await dataProvider.getMany("users", { ids: missingIds });
        const records = Array.isArray(resp?.data) ? (resp.data as unknown[]) : [];
        const next = { ...usersById };
        for (const raw of records) {
          const record = parseUserRecord(raw);
          if (!record) continue;
          const id = getUserIdFromRecord(record);
          if (!id) continue;
          next[id] = record;
        }
        setUsersById(next);
      } finally {
        setUsersLoading(false);
      }
    };
    fetchUsers();
  }, [dataProvider, userIds, usersById]);

  React.useEffect(() => {
    if (cargoIds.length === 0) return;

    let cancelled = false;
    const missingIds = cargoIds.filter((id) => !cargosById[id]);
    if (missingIds.length === 0) return;

    const fetchCargos = async () => {
      try {
        setCargosLoading(true);
        const responses = await Promise.all(
          missingIds.map(async (id) => {
            try {
              const response = await dataProvider.getOne("validCargos", { id });
              return { id, cargo: normalizeBillingCargoSummary(response.data) };
            } catch {
              return { id, cargo: null };
            }
          }),
        );

        if (cancelled) return;

        setCargosById((prev) => {
          const next = { ...prev };
          for (const item of responses) {
            if (item.cargo) {
              next[item.id] = item.cargo;
            }
          }
          return next;
        });
      } finally {
        if (cancelled) return;
        setCargosLoading(false);
      }
    };

    void fetchCargos();

    return () => {
      cancelled = true;
    };
  }, [cargoIds, cargosById, dataProvider]);

  const handleOpenDetail = (record: BillingRecord) => {
    const value = parseStringValue(getFirstDefined([(record as any).id, (record as any)._id]));
    if (!value) return;
    setSelectedBillId(value);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
  };

  return (
    <UsersContext.Provider value={usersById}>
      {usersLoading && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            正在加载本页用户信息...
          </Typography>
        </Box>
      )}
      {cargosLoading && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            正在加载本页货源信息...
          </Typography>
        </Box>
      )}
      <Datagrid
        rowClick={(_, __, record) => {
          handleOpenDetail(record as BillingRecord);
          return false as const;
        }}
        bulkActionButtons={false}
      >
        <FunctionField
          label="账单信息"
          render={(record: BillingRecord) => {
            const billId = parseStringValue(getFirstDefined([(record as any).id]));
            const title = parseStringValue(getFirstDefined([(record as any).title]));
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5, minWidth: 300 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    账单ID
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {billId || "-"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    付款对象
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                    {title || "-"}
                  </Typography>
                </Box>
              </Box>
            );
          }}
        />
        <FunctionField
          label="收付款人"
          render={(record: BillingRecord) => {
            const payerId = normalizeUserId(getFirstDefined([(record as any).payer, (record as any).payer_id]));
            const payeeId = normalizeUserId(getFirstDefined([(record as any).payee, (record as any).payee_id]));
            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5, minWidth: 320 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    付款方
                  </Typography>
                  <UserInlineCell userId={payerId} />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">
                    收款方
                  </Typography>
                  <UserInlineCell userId={payeeId} />
                </Box>
              </Box>
            );
          }}
        />
        <FunctionField
          label="金额"
          sortBy="amount"
          render={(record: BillingRecord) => {
            const amount = getFirstDefined([(record as any).amount, (record as any).total_amount, (record as any).money]);
            return formatFenToYuan(amount);
          }}
        />
        <FunctionField
          label="关联货源/车辆"
          render={(record: BillingRecord) => {
            const baseCargoSummary = parseBillingCargo(record);
            const cargoId = getBillingCargoId(record);
            const cargoSummary = mergeBillingCargoSummary(baseCargoSummary, cargoId ? cargosById[cargoId] ?? null : null);
            const truckSummary = parseBillingTruck(record);

            if (!cargoSummary && !truckSummary) {
              return (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              );
            }

            return (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 0.5, minWidth: 280 }}>
                {cargoSummary ? <BillingCargoCard cargo={cargoSummary} /> : null}
                {truckSummary ? <BillingTruckCard truck={truckSummary} /> : null}
              </Box>
            );
          }}
        />
        <FunctionField
          label="类型"
          sortBy="type"
          render={(record: BillingRecord) => {
            const typeRaw = getFirstDefined([(record as any).type, (record as any).bill_type]);
            const type = parseNumberValue(typeRaw);
            if (type === null) return <Typography variant="body2" color="text.secondary">-</Typography>;
            return (
              <Chip
                label={getBillTypeTitle(type)}
                size="small"
                sx={{
                  color: "#fff",
                  backgroundColor: getBillTypeColor(type),
                }}
              />
            );
          }}
        />
        <FunctionField
          label="状态"
          sortBy="status"
          render={(record: BillingRecord) => {
            const statusRaw = getFirstDefined([(record as any).status, (record as any).bill_status]);
            const status = parseNumberValue(statusRaw);
            if (status === null) return <Typography variant="body2" color="text.secondary">-</Typography>;
            return (
              <Chip
                label={getBillStatusTitle(status)}
                size="small"
                sx={{
                  color: "#fff",
                  backgroundColor: getBillStatusColor(status),
                }}
              />
            );
          }}
        />
        <TextField source="channel" label="渠道" />
        <FunctionField
          label="创建时间"
          sortBy="created_at"
          render={(record: BillingRecord) => {
            const date = getFirstDefined([(record as any).created_at, (record as any).createdAt, (record as any).created]);
            return date ? formatDate(date as any) : "-";
          }}
        />
      </Datagrid>
      <OnlineBillDetailDialog
        open={detailOpen}
        billId={selectedBillId}
        onClose={handleCloseDetail}
      />
    </UsersContext.Provider>
  );
};

export const OnlineBillsList: React.FC = (props: any) => {
  return (
    <List {...props} actions={<ListActions />}>
      <BillsDatagrid />
    </List>
  );
};

export default OnlineBillsList;
